import { Button, Grid, Typography } from '@material-ui/core';
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import { makeStyles } from '@material-ui/styles';
import React from 'react';
import { useHistory } from 'react-router-dom';
import SearchBar from 'src/components/SearchBar';

const useStyles = makeStyles(theme => ({
  root: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
  },
  spacing: {
    marginBottom: '24px',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dFlex: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  button: {
    marginLeft: '8px',
    height: '32px',
    backgroundColor: '#5850EC',
    color: '#ffffff',
    boxShadow: '0px 1px 1px rgba(0, 0, 0, 0.14)',
    '& hover': {
      backgroundColor: '#5850EC',
    },
  },
  smallButton: {
    height: '36px',
  },
  defaultButton: {
    backgroundColor: '#5850EC',
    display: 'flex',
    alignItems: 'center',
    width: '7.25rem',
    justifyContent: 'space-around',
  },
  sendForReviewButton: {
    marginLeft: '8px',
    height: '32px',
    backgroundColor: '#5850EC',
    color: '#f5f5f5',
    boxShadow: 'none',

    '&:hover': {
      backgroundColor: '#f5f5f5',
      color: '#5850EC',
      border: '1px solid #5850EC',
    },
  },
  review: {
    textAlign: 'right',
    borderRight: '1px solid #ddd',
    padding: '0 30px',
  },
  reviewLast: {
    border: 'none',
  },
  fontH5: {
    fontSize: '18px',
    fontWeight: '600',
    margin: '0',
  },
  fontH6: {
    fontSize: '12px',
    fontWeight: '600',
    opacity: '0.3',
    margin: '0',
  },
  back: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '0',
  },
  subtitle: {
    paddingLeft: '0px',
  },
  backArrow: {
    marginLeft: '8px',
  },
  cursorPointer: {
    cursor: 'pointer',
  },
  titleWidth: {
    wordBreak: 'break-word',
  },
  subCategorySelectionHead: {
    color: '#5850EC',
    marginLeft: '8px',
  },
}));

function CustomHeader({
  headerConfig,
  headerData,
  onSearchClick,
  onSearchChange,
  onButtonClick,
  onSecondaryButtonClick,
  className = '',
}) {
  const classes = useStyles();

  const history = useHistory();

  const backButtonClick = () => {
    if (headerConfig.backClick) {
      history.push(headerConfig.backClick);
    } else {
      history.goBack();
    }
  };

  const getTitle = (elementConfig, index) => {
    const { title, extraTitle } = headerData;
    return (
      <React.Fragment key={`${title}-${index}`}>
        <Grid container>
          <Grid item>
            <Typography className={classes.titleWidth} component="h1" variant="h3" key={index}>
              {title}
            </Typography>
          </Grid>
          {extraTitle && (
            <Grid item>
              <Typography
                component="h1"
                variant="h3"
                color="primary"
                classes={{
                  colorPrimary: classes.subCategorySelectionHead,
                }}
              >
                {`[${extraTitle}]`}
              </Typography>
            </Grid>
          )}
        </Grid>
      </React.Fragment>
    );
  };

  const getSubTitle = (elementConfig, index) => {
    const { showBackArrow } = elementConfig;
    const { subtitle } = headerData;
    return (
      <Typography
        key={index}
        className={classes.back}
        component="h2"
        gutterBottom
        variant="overline"
      >
        {showBackArrow ? (
          <KeyboardBackspaceIcon
            className={classes.cursorPointer}
            onClick={() => backButtonClick()}
          />
        ) : null}
        <span className={`${showBackArrow ? classes.backArrow : classes.subtitle}`}>
          {' '}
          {subtitle}{' '}
        </span>
      </Typography>
    );
  };

  const getButton = (elementConfig, index, noOfElements) => {
    const {
      buttonText,
      disabled,
      variant,
      color,
      size,
      className,
      startIcon,
      icon: Icon,
    } = elementConfig;

    return (
      <Button
        key={index}
        variant={variant || 'outlined'}
        color={color || 'secondary'}
        disabled={disabled}
        startIcon={startIcon || ''}
        className={`${
          buttonText === 'Send for Review'
            ? classes.sendForReviewButton
            : size === 'default'
            ? classes.defaultButton
            : classes.button
        } ${className ? classes[className] : ''}`}
        size={size}
        onClick={
          index === noOfElements - 1 && onSecondaryButtonClick
            ? onSecondaryButtonClick
            : onButtonClick
        }
      >
        {Icon && <Icon fontSize="small" />}
        {buttonText}
      </Button>
    );
  };

  const getInfoTitle = (elementConfig, index) => {
    const { infoText, dataKeyMap } = elementConfig;
    return (
      <div className={classes.review} key={index}>
        <Typography className={classes.fontH5} variant="h5" gutterBottom>
          {headerData[dataKeyMap]}
        </Typography>
        <Typography className={classes.fontH6} variant="h6" gutterBottom>
          {infoText}
        </Typography>
      </div>
    );
  };

  const getSearch = (elementConfig, index) => {
    const { placeholder, buttontext, hidebutton } = elementConfig;

    return (
      <Grid container spacing={3} justify="center" alignItems="center" key={index}>
        <Grid item xs={12}>
          <SearchBar
            onSearch={onSearchClick}
            onChange={onSearchChange}
            placeholder={placeholder}
            buttontext={buttontext}
            hidebutton={hidebutton}
          />
        </Grid>
      </Grid>
    );
  };

  const buildElements = block => {
    const { elementsList } = block;

    const headElements = elementsList.map((elementConfig, index) => {
      const { elementType } = elementConfig;

      switch (elementType) {
        case 'subTitle':
          return getSubTitle(elementConfig, index);
        case 'title':
          return getTitle(elementConfig, index);
        case 'infoTitle':
          return getInfoTitle(elementConfig, index);
        case 'button':
          return getButton(elementConfig, index, elementsList.length || 0);
        case 'search':
          return getSearch(elementConfig, index);
        default:
          return <></>;
      }
    });

    return headElements;
  };

  return (
    <div className={`${classes.spacing} ${className}`}>
      {headerConfig &&
        headerConfig.blocks &&
        headerConfig.blocks.map((block, index) => (
          <div key={index} className={classes[block.className]}>
            {buildElements(block)}
          </div>
        ))}
    </div>
  );
}

export { CustomHeader };
